import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  MapPin, Navigation, CheckCircle, Clock, ChevronDown, ChevronUp, ExternalLink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Custom numbered marker icons
const createIcon = (num, color) => L.divIcon({
  className: "",
  html: `<div style="background:${color};color:white;width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3)">${num}</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const RouteMapPanel = ({ inspectorName }) => {
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [completing, setCompleting] = useState(null);

  const fetchRoute = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/route/active/${encodeURIComponent(inspectorName)}`);
      setRoute(res.data);
    } catch {} finally { setLoading(false); }
  }, [inspectorName]);

  useEffect(() => { fetchRoute(); }, [fetchRoute]);

  const completeStop = async (stopNum) => {
    setCompleting(stopNum);
    try {
      await axios.post(`${API}/route/complete-stop`, {
        inspector_name: inspectorName,
        stop_number: stopNum,
      });
      fetchRoute();
    } catch {} finally { setCompleting(null); }
  };

  const openNavigation = (lat, lng, address) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    window.open(url, "_blank");
  };

  if (loading) return <div className="text-center py-4 text-xs text-gray-400">Loading route...</div>;
  if (!route?.active) return null;

  const stops = route.stops || [];
  const hasCoords = stops.some(s => s.lat && s.lng);
  const completed = stops.filter(s => s.status === "completed").length;
  const pending = stops.filter(s => s.status === "pending");
  const currentStop = pending[0];
  const nextStop = pending[1];
  const center = hasCoords ? [stops.find(s => s.lat)?.lat || 42.3, stops.find(s => s.lng)?.lng || -71.8] : [42.3, -71.8];
  const polyline = stops.filter(s => s.lat && s.lng).map(s => [s.lat, s.lng]);

  return (
    <div className="space-y-3" data-testid="route-map-panel">
      {/* Summary Bar */}
      <div className="bg-[#1a2332] rounded-lg p-3 text-white" data-testid="route-summary">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin className="h-4 w-4 text-[#4DA3FF]" />
            <div>
              <p className="text-sm font-bold">Active Route — {route.route_date}</p>
              <p className="text-[10px] text-gray-400">{route.source_file}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-center">
            <div><p className="text-lg font-bold">{route.total_stops}</p><p className="text-[8px] text-gray-400 uppercase">Stops</p></div>
            <div><p className="text-lg font-bold text-[#6B8F62]">{completed}</p><p className="text-[8px] text-gray-400 uppercase">Done</p></div>
            <div><p className="text-lg font-bold text-[#B08A3E]">{pending.length}</p><p className="text-[8px] text-gray-400 uppercase">Remaining</p></div>
          </div>
          <button onClick={() => setExpanded(!expanded)} className="p-1 hover:bg-white/10 rounded">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
        {/* Current / Next Stop */}
        {currentStop && (
          <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-[9px] text-[#4DA3FF] uppercase font-bold">Current Stop #{currentStop.stop_number}</p>
              <p className="text-xs">{currentStop.address}, {currentStop.city}</p>
              <p className="text-[9px] text-gray-400">{currentStop.order_number}</p>
            </div>
            {currentStop.lat && currentStop.lng ? (
              <button onClick={() => openNavigation(currentStop.lat, currentStop.lng, currentStop.address)}
                className="px-3 py-1.5 bg-[#4DA3FF] hover:bg-[#3B8DE0] text-white rounded-lg text-xs font-semibold flex items-center gap-1.5"
                data-testid="navigate-btn">
                <Navigation className="h-3.5 w-3.5" /> Navigate
              </button>
            ) : null}
            <button onClick={() => completeStop(currentStop.stop_number)} disabled={completing === currentStop.stop_number}
              className="px-3 py-1.5 bg-[#6B8F62] hover:bg-[#5A7A53] text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
              data-testid="complete-stop-btn">
              <CheckCircle className="h-3.5 w-3.5" /> {completing ? "..." : "Done"}
            </button>
          </div>
        )}
        {nextStop && (
          <p className="text-[9px] text-gray-500 mt-1">Next: #{nextStop.stop_number} {nextStop.address}, {nextStop.city}</p>
        )}
      </div>

      {expanded && (
        <>
          {/* Map */}
          {hasCoords && (
            <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: 300 }} data-testid="route-map">
              <MapContainer center={center} zoom={10} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
                {stops.filter(s => s.lat && s.lng).map(s => (
                  <Marker key={s.stop_number} position={[s.lat, s.lng]}
                    icon={createIcon(s.stop_number, s.status === "completed" ? "#6B8F62" : "#2F5D8A")}>
                    <Popup>
                      <div className="text-xs">
                        <p className="font-bold">Stop #{s.stop_number}</p>
                        <p>{s.address}, {s.city}</p>
                        <p className="text-gray-400">{s.order_number}</p>
                        <p className={s.status === "completed" ? "text-green-600 font-bold" : "text-blue-600"}>{s.status.toUpperCase()}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {polyline.length > 1 && <Polyline positions={polyline} color="#2F5D8A" weight={3} opacity={0.7} dashArray="8,6" />}
              </MapContainer>
            </div>
          )}

          {/* Stop List */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="route-stop-list">
            <div className="max-h-[250px] overflow-y-auto">
              <table className="w-full text-[10px]">
                <thead className="sticky top-0 bg-gray-50"><tr className="border-b">
                  <th className="px-2 py-1.5 text-left font-semibold text-gray-500">#</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-gray-500">Order</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-gray-500">Address</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-gray-500">City</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-gray-500">Status</th>
                  <th className="px-2 py-1.5 text-center font-semibold text-gray-500">Actions</th>
                </tr></thead>
                <tbody>{stops.map(s => (
                  <tr key={s.stop_number} className={`border-b border-gray-50 ${s.status === "completed" ? "bg-[#6B8F62]/5" : ""}`}
                    data-testid={`route-stop-${s.stop_number}`}>
                    <td className="px-2 py-1.5">
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold text-white ${s.status === "completed" ? "bg-[#6B8F62]" : "bg-[#2F5D8A]"}`}>
                        {s.stop_number}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 font-medium text-gray-700">{s.order_number}</td>
                    <td className="px-2 py-1.5 text-gray-500 max-w-[150px] truncate">{s.address}</td>
                    <td className="px-2 py-1.5 text-gray-400">{s.city}</td>
                    <td className="px-2 py-1.5">
                      {s.status === "completed" ? (
                        <span className="px-1.5 py-0.5 bg-[#6B8F62] text-white rounded text-[8px] font-bold">DONE</span>
                      ) : (
                        <span className="px-1.5 py-0.5 bg-[#2F5D8A]/10 text-[#2F5D8A] rounded text-[8px] font-bold">PENDING</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {s.lat && s.lng && (
                          <button onClick={() => openNavigation(s.lat, s.lng, s.address)}
                            className="p-1 text-[#4DA3FF] hover:bg-[#4DA3FF]/10 rounded" title="Navigate">
                            <Navigation className="h-3 w-3" />
                          </button>
                        )}
                        {s.status === "pending" && (
                          <button onClick={() => completeStop(s.stop_number)} disabled={completing === s.stop_number}
                            className="p-1 text-[#6B8F62] hover:bg-[#6B8F62]/10 rounded disabled:opacity-50" title="Mark Done">
                            <CheckCircle className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RouteMapPanel;
